export type KhaataEntryType = 'expense' | 'purchase' | 'asset' | 'liability';

export interface CategoryOption {
  value: string;
  label: string;
  type: KhaataEntryType;
  /** true for built-in categories that ship with the app (cannot be deleted) */
  isDefault?: boolean;
  /** When false, hidden from Add/Edit entry dropdown. Defaults to true. */
  enabled?: boolean;
}

export interface CategoryGroup {
  type: KhaataEntryType;
  label: string;
  categories: CategoryOption[];
}

export const KHAATA_TYPES: { value: KhaataEntryType; label: string; hint: string }[] = [
  { value: 'expense', label: 'Expense', hint: 'Day-to-day operating costs' },
  { value: 'purchase', label: 'Purchase', hint: 'Inventory & supplies bought from vendors' },
  { value: 'asset', label: 'Asset', hint: 'Long-term items you own' },
  { value: 'liability', label: 'Liabilities & Statutory', hint: 'Money you owe or pay on behalf of others' },
];

const TYPE_LABELS: Record<KhaataEntryType, string> = {
  expense: 'Expense',
  purchase: 'Purchase',
  asset: 'Asset',
  liability: 'Liabilities & Statutory',
};

const VALID_TYPES: KhaataEntryType[] = ['expense', 'purchase', 'asset', 'liability'];

/**
 * Which extra fields each type needs in the Add Entry drawer.
 * Category selection derives the type, which drives these fields.
 */
export const TYPE_FIELD_CONFIG: Record<
  KhaataEntryType,
  { supplier: boolean; invoice: boolean; dueDate: boolean }
> = {
  expense: { supplier: false, invoice: false, dueDate: false },
  purchase: { supplier: true, invoice: true, dueDate: true },
  asset: { supplier: true, invoice: true, dueDate: false },
  liability: { supplier: false, invoice: true, dueDate: true },
};

export const PURCHASE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'food-grocery', label: 'Food & Grocery' },
  { value: 'vegetables-fruits', label: 'Vegetables & Fruits' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'meat-poultry', label: 'Meat & Poultry' },
  { value: 'spices-condiments', label: 'Spices & Condiments' },
  { value: 'dry-goods-staples', label: 'Dry Goods & Staples' },
  { value: 'frozen-foods', label: 'Frozen Foods' },
  { value: 'bakery-ingredients', label: 'Bakery Ingredients' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'liquor-alcohol', label: 'Liquor & Alcohol' },
  { value: 'lpg-cooking-gas', label: 'LPG / Cooking Gas' },
  { value: 'coal-charcoal', label: 'Coal & Charcoal' },
  { value: 'packaging-materials', label: 'Packaging Materials' },
  { value: 'disposables-takeaway', label: 'Disposables (Takeaway)' },
  { value: 'cleaning-consumables', label: 'Cleaning Consumables' },
  { value: 'hotel-guest-supplies', label: 'Hotel Guest Supplies' },
  { value: 'toiletries-amenities', label: 'Toiletries & Amenities' },
  { value: 'kitchen-consumables', label: 'Kitchen Consumables' },
  { value: 'linen-housekeeping-supplies', label: 'Linen & Housekeeping Supplies' },
  { value: 'stationery-office-consumables', label: 'Stationery & Office Consumables' },
  { value: 'other-purchases', label: 'Other Purchases' },
];

export const EXPENSE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'salaries-wages', label: 'Salaries & Wages' },
  { value: 'employee-expenses', label: 'Employee Expenses' },
  { value: 'staff-meals-welfare', label: 'Staff Meals & Welfare' },
  { value: 'utility-expenses', label: 'Utility Expenses' },
  { value: 'electricity-bills', label: 'Electricity Bills' },
  { value: 'water-charges', label: 'Water Charges' },
  { value: 'fuel-expenses', label: 'Fuel Expenses' },
  { value: 'repairs-maintenance', label: 'Repairs & Maintenance' },
  { value: 'pest-control', label: 'Pest Control' },
  { value: 'marketing-advertising', label: 'Marketing & Advertising' },
  { value: 'aggregator-commissions', label: 'Aggregator Commissions (Swiggy/Zomato)' },
  { value: 'ota-commissions', label: 'OTA Commissions (MMT/Booking)' },
  { value: 'office-expenses', label: 'Office Expenses' },
  { value: 'professional-legal-fees', label: 'Professional & Legal Fees' },
  { value: 'government-fees-licenses', label: 'Government Fees & Licenses' },
  { value: 'banking-finance-charges', label: 'Banking & Finance Charges' },
  { value: 'software-subscriptions', label: 'Software & Subscriptions' },
  { value: 'travel-transportation', label: 'Travel & Transportation' },
  { value: 'housekeeping-laundry', label: 'Housekeeping & Laundry' },
  { value: 'security-expenses', label: 'Security Expenses' },
  { value: 'rent-lease', label: 'Rent & Lease' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'miscellaneous-expenses', label: 'Miscellaneous Expenses' },
];

export const ASSET_CATEGORIES: { value: string; label: string }[] = [
  { value: 'air-conditioner', label: 'AC' },
  { value: 'fridge', label: 'Fridge' },
  { value: 'freezer', label: 'Freezer / Deep Freezer' },
  { value: 'generator', label: 'Generator' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'mattress-bedding', label: 'Mattress & Bedding' },
  { value: 'television', label: 'TV' },
  { value: 'computer', label: 'Computer' },
  { value: 'pos-billing-system', label: 'POS & Billing System' },
  { value: 'cctv-security', label: 'CCTV & Security System' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'building', label: 'Building' },
  { value: 'kitchen-equipment', label: 'Kitchen Equipment' },
  { value: 'crockery-cutlery', label: 'Crockery & Cutlery' },
  { value: 'dishwasher', label: 'Dishwasher' },
  { value: 'water-heater', label: 'Water Heater / Geyser' },
  { value: 'water-purifier', label: 'Water Purifier (RO)' },
  { value: 'signage-branding', label: 'Signage & Branding' },
  { value: 'other-assets', label: 'Other Assets' },
];

export const LIABILITY_CATEGORIES: { value: string; label: string }[] = [
  { value: 'gst-payment', label: 'GST Payment' },
  { value: 'gst-collected', label: 'GST Collected' },
  { value: 'gst-input-credit', label: 'GST Input Credit' },
  { value: 'tds', label: 'TDS' },
  { value: 'pf-contribution', label: 'PF Contribution' },
  { value: 'esi-contribution', label: 'ESI Contribution' },
  { value: 'professional-tax', label: 'Professional Tax' },
  { value: 'loan-emi', label: 'Loan EMI' },
  { value: 'loan-disbursement', label: 'Loan Disbursement' },
  { value: 'security-deposit', label: 'Security Deposit' },
  { value: 'other-liabilities', label: 'Other Liabilities & Statutory' },
];

/** Default category the drawer selects for a fresh entry. */
export const DEFAULT_CATEGORY_VALUE = 'miscellaneous-expenses';

/** Flat list of all built-in categories tagged with their type. */
export const DEFAULT_CATEGORIES: CategoryOption[] = [
  ...EXPENSE_CATEGORIES.map((c) => ({ ...c, type: 'expense' as const, isDefault: true, enabled: true })),
  ...PURCHASE_CATEGORIES.map((c) => ({ ...c, type: 'purchase' as const, isDefault: true, enabled: true })),
  ...ASSET_CATEGORIES.map((c) => ({ ...c, type: 'asset' as const, isDefault: true, enabled: true })),
  ...LIABILITY_CATEGORIES.map((c) => ({ ...c, type: 'liability' as const, isDefault: true, enabled: true })),
];

const defaultCategoryMap = new Map(DEFAULT_CATEGORIES.map((c) => [c.value, c]));

export function normalizeKhaataType(type?: string): KhaataEntryType {
  return VALID_TYPES.includes(type as KhaataEntryType)
    ? (type as KhaataEntryType)
    : 'expense';
}

export function getTypeLabel(type?: string): string {
  return TYPE_LABELS[normalizeKhaataType(type)];
}

/** Turn a display name into a stable slug for storage. */
export function slugifyCategory(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function sortByLabel<T extends { label: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Merge built-in defaults with restaurant custom categories (custom overrides
 * default when values collide), returned sorted alphabetically by label.
 */
export function isCategoryEnabled(category: CategoryOption): boolean {
  return category.enabled !== false;
}

export function buildCategoryOptions(custom: CategoryOption[] = []): CategoryOption[] {
  const byValue = new Map<string, CategoryOption>();
  DEFAULT_CATEGORIES.forEach((c) => byValue.set(c.value, c));
  custom.forEach((c) => {
    if (!c.value) return;
    const existing = byValue.get(c.value);
    byValue.set(c.value, {
      ...(existing || c),
      ...c,
      isDefault: existing?.isDefault ?? false,
      enabled: c.enabled ?? existing?.enabled ?? true,
    });
  });
  return sortByLabel(Array.from(byValue.values()));
}

/** Enabled categories only — used in Add/Edit entry dropdown. */
export function getEnabledCategoriesForEntry(
  options: CategoryOption[] = DEFAULT_CATEGORIES
): CategoryOption[] {
  return sortByLabel(options.filter(isCategoryEnabled));
}

/**
 * Categories grouped by type (alphabetical within each group).
 * When `selectedValue` is set, that category is always included even if disabled
 * (so editing an old entry with a now-disabled category still works).
 */
export function getCategoriesGroupedForEntry(
  options: CategoryOption[] = DEFAULT_CATEGORIES,
  selectedValue?: string
): CategoryGroup[] {
  const enabled = options.filter(
    (c) => isCategoryEnabled(c) || c.value === selectedValue
  );

  return KHAATA_TYPES.map((typeDef) => ({
    type: typeDef.value,
    label: typeDef.label,
    categories: sortByLabel(
      enabled.filter((c) => normalizeKhaataType(c.type) === typeDef.value)
    ),
  })).filter((group) => group.categories.length > 0);
}

export function getCategoriesForType(
  type: KhaataEntryType,
  options: CategoryOption[] = DEFAULT_CATEGORIES
): CategoryOption[] {
  return sortByLabel(options.filter((c) => normalizeKhaataType(c.type) === type));
}

export function getAllCategoriesSorted(
  options: CategoryOption[] = DEFAULT_CATEGORIES
): CategoryOption[] {
  return sortByLabel(options);
}

/** Derive the entry type from the selected category value. */
export function getTypeForCategory(
  value: string,
  options: CategoryOption[] = DEFAULT_CATEGORIES
): KhaataEntryType {
  const found = options.find((c) => c.value === value) || defaultCategoryMap.get(value);
  return found ? normalizeKhaataType(found.type) : 'expense';
}

/** Human-readable label for a stored category slug. */
export function formatCategoryLabel(
  category?: string,
  options?: CategoryOption[]
): string {
  if (!category) return 'Uncategorized';
  const fromOptions = options?.find((c) => c.value === category);
  if (fromOptions) return fromOptions.label;
  const fromDefault = defaultCategoryMap.get(category);
  if (fromDefault) return fromDefault.label;
  return category
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
